// src/components/ModalVerOrdenPago.jsx
import { useEffect, useState } from "react";
import { Modal, Button, Table, Row, Col, Badge } from "react-bootstrap";
import moment from "moment";
import { getOrdenPago, anularOrdenPago } from "../helpers/rutaOrdenesPago";
import { generarPDFOrdenPago } from "../helpers/pdfOrdenPago";

export default function ModalVerOrdenPago({ show, onHide, opId, opData }) {
  const [loading, setLoading] = useState(false);
  const [op, setOp] = useState(null);

  const fmt = (n) =>
    (Number(n || 0)).toLocaleString("es-AR", { style: "currency", currency: "ARS" });

  const estadoUpper = (s) => (s || "").toString().toUpperCase();

  useEffect(() => {
    if (!show) return;
    (async () => {
      setLoading(true);
      try {
        if (opData) {
          setOp(opData);
        } else if (opId) {
          const resp = await getOrdenPago(opId);
          if (resp?.ok) setOp(resp.orden);
          else alert(resp?.msg || "No se pudo obtener la orden de pago.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [show, opId, opData]);

  const handleAnular = async () => {
    if (!op?._id) return;
    if (!window.confirm("¿Seguro que querés ANULAR esta Orden de Pago?")) return;
    const resp = await anularOrdenPago(op._id);
    if (resp?.ok) {
      alert("Orden de pago anulada y aplicaciones revertidas.");
      setOp(resp.orden); // refleja estado = anulada
      if (onHide) onHide(true);
    } else {
      alert(resp?.msg || "No se pudo anular la orden.");
    }
  };

  const sumPagos = (op?.pagos || []).reduce((a, m) => a + Number(m.importe || 0), 0);
  const sumRets  = (op?.retenciones || []).reduce((a, r) => a + Number(r.importe || 0), 0);
  const sumApls  = (op?.aplicaciones || []).reduce((a, ap) => a + Number(ap.aplicado || 0), 0);

  const tot = op?.totales || {};
  const totalAplicado  = tot.aplicado ?? sumApls;
  const totalPagos     = sumPagos;
  const totalRets      = tot.retenciones ?? sumRets;
  const totalNeto      = tot.netoPagado ?? (sumPagos - sumRets);

  return (
    <Modal show={show} onHide={() => onHide && onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Orden de Pago {op?.numero ? `#${op.numero}` : ""}
          {" "}
          {estadoUpper(op?.estado) === "ANULADA" && <Badge bg="danger">ANULADA</Badge>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && <div>Cargando...</div>}
        {!loading && op && (
          <>
            <Row className="mb-2">
              <Col md={6}>
                <b>Tercero:</b> {op.tercero?.transporte || "-"}
              </Col>
              <Col md={3}>
                <b>Fecha:</b> {op.fecha ? moment(op.fecha).format("DD/MM/YYYY") : "-"}
              </Col>
              <Col md={3}>
                <b>Estado:</b> {estadoUpper(op.estado) || "-"}
              </Col>
            </Row>
            {op.cliente && (
              <Row className="mb-2">
                <Col md={12}>
                  <b>Cliente:</b> {op.cliente?.razonsocial || "-"}
                  {op.cliente?.cuit ? ` • CUIT: ${op.cliente.cuit}` : ""}
                </Col>
              </Row>
            )}
            {op.observaciones && (
              <Row className="mb-2">
                <Col><b>Observaciones:</b> {op.observaciones}</Col>
              </Row>
            )}

            <h6 className="mt-3">Aplicaciones (Comandas)</h6>
            <Table size="sm" bordered hover>
              <thead>
                <tr>
                  <th>Comanda</th>
                  <th>Viaje</th>
                  <th>Fecha</th>
                  <th style={{ textAlign: "right" }}>Pactado (snapshot)</th>
                  <th style={{ textAlign: "right" }}>Pagado anterior</th>
                  <th style={{ textAlign: "right" }}>Aplicado</th>
                </tr>
              </thead>
              <tbody>
                {(op.aplicaciones || []).map((a, i) => {
                  const c = a.comanda || {};
                  return (
                    <tr key={i}>
                      <td>{c.codcom != null ? String(c.codcom).padStart(6, "0") : c._id}</td>
                      <td>{c.nroviaje || ""}</td>
                      <td>{c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}</td>
                      <td style={{ textAlign: "right" }}>{fmt(a.pactado)}</td>
                      <td style={{ textAlign: "right" }}>{fmt(a.pagadoAnterior)}</td>
                      <td style={{ textAlign: "right" }}>{fmt(a.aplicado)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <Row className="mt-3">
              <Col md={6}>
                <h6>Pagos</h6>
                <Table size="sm" bordered hover>
                  <thead>
                    <tr>
                      <th>Medio</th>
                      <th>Detalle</th>
                      <th>Fecha cheque</th>
                      <th style={{ textAlign: "right" }}>Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(op.pagos || []).map((m, i) => (
                      <tr key={i}>
                        <td>{m.medio}</td>
                        <td>{[m.banco, m.referencia || m.nroCheque].filter(Boolean).join(" • ")}</td>
                        <td>{m.fechaCheque ? moment(m.fechaCheque).format("DD/MM/YYYY") : ""}</td>
                        <td style={{ textAlign: "right" }}>{fmt(m.importe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>

              <Col md={6}>
                <h6>Retenciones</h6>
                <Table size="sm" bordered hover>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Base</th>
                      <th>%</th>
                      <th>Comprobante</th>
                      <th style={{ textAlign: "right" }}>Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(op.retenciones || []).map((r, i) => (
                      <tr key={i}>
                        <td>{r.tipo}</td>
                        <td style={{ textAlign: "right" }}>{fmt(r.base)}</td>
                        <td style={{ textAlign: "right" }}>{(Number(r.porcentaje || 0)).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                        <td>{r.comprobante || ""}</td>
                        <td style={{ textAlign: "right" }}>{fmt(r.importe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md={3}><b>Σ Aplicado:</b> {fmt(totalAplicado)}</Col>
              <Col md={3}><b>Σ Pagos:</b> {fmt(totalPagos)}</Col>
              <Col md={3}><b>Σ Retenciones:</b> {fmt(totalRets)}</Col>
              <Col md={3}><b>Σ Neto Pagado:</b> {fmt(totalNeto)}</Col>
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide && onHide(false)}>Cerrar</Button>
        <Button
          variant="primary"
          onClick={() => generarPDFOrdenPago(op?._id)}
          disabled={!op?._id}
        >
          Generar PDF
        </Button>
        <Button
          variant="danger"
          onClick={handleAnular}
          disabled={!op || estadoUpper(op.estado) === "ANULADA"}
        >
          Anular OP
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
